import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Popconfirm,
  Row,
  Space,
  Spin,
  Table,
  message
} from 'antd';
import {
  CheckCircleOutlined,
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  UndoOutlined
} from '@ant-design/icons';
import axios from 'axios';

const DutyAreaSettings = ({ classId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState({});
  const [editingAreas, setEditingAreas] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (!classId) return;
    fetchTemplates();
    fetchClassAreas();
  }, [classId]);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const buildEditingAreas = (areas, keyPrefix = 'area') =>
    areas.map((area, index) => ({
      key: area.id ? `existing-${area.id}` : `${keyPrefix}-${index}`,
      id: area.id || null,
      area_name: area.area_name || '',
      area_description: area.area_description || '',
      sort_order: area.sort_order ?? index + 1
    }));

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v2/duty-areas/templates', {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        setTemplates(response.data.data || {});
      }
    } catch (error) {
      console.error('Failed to fetch duty area templates:', error);
      message.error('获取模板失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassAreas = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v2/duty-areas/${classId}`, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        setEditingAreas(buildEditingAreas(response.data.data || []));
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Failed to fetch class duty areas:', error);
      message.error(error.response?.data?.message || '获取区域配置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (type, template) => {
    const templateAreas = (template?.areas || []).map((area, index) => ({
      key: `template-${type}-${index}`,
      id: null,
      area_name: area.area_name || '',
      area_description: area.area_description || '',
      sort_order: area.sort_order ?? index + 1
    }));

    setSelectedTemplate({
      type,
      name: template?.name || '',
      originalAreas: templateAreas
    });
    setEditingAreas(templateAreas);
    setHasUnsavedChanges(true);
    message.info(`已选择${template?.name || '模板'}，保存后生效`);
  };

  const handleRestoreTemplate = () => {
    if (!selectedTemplate?.originalAreas) {
      message.warning('请先选择模板');
      return;
    }

    setEditingAreas(
      selectedTemplate.originalAreas.map((area, index) => ({
        ...area,
        key: `template-${selectedTemplate.type}-${index}`
      }))
    );
    setHasUnsavedChanges(true);
    message.success('已恢复模板默认内容');
  };

  const handleClearAll = () => {
    setEditingAreas([]);
    setSelectedTemplate(null);
    setHasUnsavedChanges(true);
  };

  const handleEditArea = (key, field, value) => {
    setEditingAreas((prev) =>
      prev.map((area) => (area.key === key ? { ...area, [field]: value } : area))
    );
    setHasUnsavedChanges(true);
  };

  const handleDeleteArea = (key) => {
    setEditingAreas((prev) =>
      prev
        .filter((area) => area.key !== key)
        .map((area, index) => ({
          ...area,
          sort_order: index + 1
        }))
    );
    setHasUnsavedChanges(true);
  };

  const handleAddArea = () => {
    setEditingAreas((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        id: null,
        area_name: '',
        area_description: '',
        sort_order: prev.length + 1
      }
    ]);
    setHasUnsavedChanges(true);
  };

  const handleSaveAll = async () => {
    const normalizedAreas = editingAreas.map((area, index) => ({
      id: area.id || null,
      area_name: String(area.area_name || '').trim(),
      area_description: String(area.area_description || '').trim(),
      sort_order: area.sort_order ?? index + 1
    }));

    if (normalizedAreas.length === 0) {
      message.warning('请先添加至少一个区域');
      return;
    }

    if (normalizedAreas.some((area) => !area.area_name)) {
      message.error('存在未填写名称的区域，请完善后再保存');
      return;
    }

    try {
      setLoading(true);
      await axios.put(
        `/api/v2/duty-areas/batch/${classId}`,
        { areas: normalizedAreas },
        { headers: getAuthHeaders() }
      );

      message.success(`已保存 ${normalizedAreas.length} 个区域`);
      setHasUnsavedChanges(false);
      await fetchClassAreas();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to save duty areas:', error);
      message.error(error.response?.data?.message || '保存区域失败');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '序号',
      width: 70,
      render: (_, __, index) => index + 1
    },
    {
      title: '区域名称',
      dataIndex: 'area_name',
      width: 220,
      render: (text, record) => (
        <Input
          value={text}
          placeholder="例如：扫地（1-2组）"
          status={!text ? 'error' : ''}
          onChange={(e) => handleEditArea(record.key, 'area_name', e.target.value)}
        />
      )
    },
    {
      title: '区域描述',
      dataIndex: 'area_description',
      render: (text, record) => (
        <Input
          value={text}
          placeholder="例如：负责教室前排区域清洁"
          onChange={(e) => handleEditArea(record.key, 'area_description', e.target.value)}
        />
      )
    },
    {
      title: '操作',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="确定删除这个区域吗？"
          okText="确定"
          cancelText="取消"
          onConfirm={() => handleDeleteArea(record.key)}
        >
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  const templateCards = [
    { key: 'basic', color: '#52c41a', emoji: 'A', suffix: '' },
    { key: 'standard', color: '#1890ff', emoji: 'B', suffix: '' },
    { key: 'complete', color: '#722ed1', emoji: 'C', suffix: '（推荐）' }
  ];

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {hasUnsavedChanges && (
          <Alert
            type="warning"
            showIcon
            closable
            message="当前有未保存的修改"
            description="区域配置改动需要点击下方“保存区域设置”后才会真正生效。"
          />
        )}

        <Card
          size="small"
          title={
            <span>
              <CheckCircleOutlined style={{ marginRight: 8 }} />
              第一步：选择模板（可选）
            </span>
          }
        >
          <Row gutter={12}>
            {templateCards.map((card) => {
              const template = templates[card.key];
              if (!template) return null;

              return (
                <Col span={8} key={card.key}>
                  <Card
                    hoverable
                    onClick={() => handleSelectTemplate(card.key, template)}
                    bodyStyle={{ padding: '16px 12px' }}
                    style={{
                      borderColor: selectedTemplate?.type === card.key ? card.color : undefined,
                      borderWidth: selectedTemplate?.type === card.key ? 2 : 1
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, marginBottom: 8 }}>{card.emoji}</div>
                      <h4 style={{ color: card.color, margin: '0 0 4px 0', fontSize: 15 }}>
                        {template.name}
                      </h4>
                      <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
                        {template.count}个区域{card.suffix}
                      </p>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>

        <Card
          size="small"
          title={
            <span>
              <EditOutlined style={{ marginRight: 8 }} />
              第二步：编辑区域配置（{editingAreas.length}个区域）
            </span>
          }
          extra={
            <Space>
              {selectedTemplate && (
                <Button size="small" icon={<UndoOutlined />} onClick={handleRestoreTemplate}>
                  恢复模板
                </Button>
              )}
              <Popconfirm
                title="确定清空当前区域配置吗？"
                okText="确定"
                cancelText="取消"
                onConfirm={handleClearAll}
              >
                <Button size="small" danger icon={<ClearOutlined />}>
                  一键清空
                </Button>
              </Popconfirm>
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAddArea}>
                添加区域
              </Button>
            </Space>
          }
        >
          {editingAreas.length === 0 ? (
            <Empty description="暂无区域配置，请选择模板或手动新增" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Table
              rowKey="key"
              size="small"
              pagination={false}
              dataSource={editingAreas}
              columns={columns}
              scroll={{ y: 400 }}
            />
          )}
        </Card>

        <Card size="small">
          <div style={{ textAlign: 'center' }}>
            <Space size="large">
              <Button
                type="primary"
                size="large"
                icon={<SaveOutlined />}
                onClick={handleSaveAll}
                disabled={editingAreas.length === 0 || !hasUnsavedChanges}
                style={{ minWidth: 220 }}
              >
                保存区域设置
              </Button>
              <div style={{ color: hasUnsavedChanges ? '#faad14' : '#52c41a', fontSize: 12 }}>
                {hasUnsavedChanges ? '有未保存的修改' : '当前内容已保存'}
              </div>
            </Space>
          </div>
        </Card>

        <Alert
          type="info"
          showIcon
          message="使用说明"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>区域配置定义了班级有哪些值日岗位，例如扫地、拖地、整理讲台等。</li>
              <li>可先套用模板，再继续删改或追加自定义区域。</li>
              <li>保存现在改成一次性提交，成功后才会整体生效。</li>
            </ul>
          }
        />
      </Space>
    </Spin>
  );
};

export default DutyAreaSettings;
